import React from 'react'
import { Minus, Plus, Trash2 } from 'lucide-react'
import { CartItem as CartItemType, useCartStore } from '../../stores/cartStore'
import { calculateCustomizedPrice } from '../../types'
import { useMenuTheme } from '@/contexts/MenuThemeContext'

interface CartItemProps {
  item: CartItemType
}

export const CartItem: React.FC<CartItemProps> = ({ item }) => {
  const { updateQuantity, removeItem } = useCartStore()
  const { theme } = useMenuTheme()

  const handleDecrement = () => {
    if (item.quantity > 1) {
      updateQuantity(item.productId, item.quantity - 1)
    }
  }

  const handleIncrement = () => {
    updateQuantity(item.productId, item.quantity + 1)
  }

  const handleRemove = () => {
    removeItem(item.productId)
  }

  // Calculate price including customizations
  const unitPrice = calculateCustomizedPrice(item.price, item.customizations ?? null)
  const itemTotal = unitPrice * item.quantity
  const hasCustomizations = item.customizations && item.customizations.selectedOptions.length > 0

  return (
    <div
      className="flex items-start gap-3 py-4"
      style={{ borderBottomColor: theme.borderColor, borderBottomWidth: '1px' }}
    >
      {/* Product Image */}
      <div
        className="w-16 h-16 shrink-0 rounded-xl overflow-hidden"
        style={{ backgroundColor: `${theme.textColor}0d` }}
      >
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-2xl">
            🍽️
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium" style={{ color: theme.textColor }}>
          {item.name}
        </h4>

        {/* Structured Customizations */}
        {hasCustomizations && (
          <div className="mt-2 space-y-1">
            {item.customizations!.selectedOptions.map((option, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between text-xs"
                style={{ color: theme.textSecondary }}
              >
                <span className="flex items-center gap-1">
                  <span className="font-medium">{option.optionGroupName}:</span>
                  <span>{option.optionName}</span>
                  {option.quantity > 1 && (
                    <span style={{ color: theme.primaryColor }}>×{option.quantity}</span>
                  )}
                </span>
                {option.priceModifier !== 0 && (
                  <span style={{ color: option.priceModifier > 0 ? '#4ade80' : '#f87171' }}>
                    {option.priceModifier > 0 ? '+' : ''}€{(option.priceModifier * option.quantity).toFixed(2)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Free Text Notes from Customizations */}
        {item.customizations?.freeTextNotes && (
          <p className="mt-1 text-xs italic" style={{ color: theme.mutedColor }}>
            {item.customizations.freeTextNotes}
          </p>
        )}

        {/* Legacy notes field */}
        {item.notes && (
          <p className="mt-1 text-xs italic" style={{ color: theme.mutedColor }}>
            Note: {item.notes}
          </p>
        )}

        {/* Price */}
        <div className="mt-2 flex items-center gap-2">
          {item.customizations?.totalPriceModifier ? (
            <>
              <span className="text-xs line-through" style={{ color: theme.mutedColor }}>
                €{item.price.toFixed(2)}
              </span>
              <span className="text-sm font-medium" style={{ color: theme.primaryColor }}>
                €{unitPrice.toFixed(2)}
              </span>
            </>
          ) : (
            <span className="text-sm font-medium" style={{ color: theme.textColor }}>
              €{item.price.toFixed(2)}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleDecrement}
          className="p-1 rounded-full transition-colors"
          style={{ backgroundColor: `${theme.textColor}0d` }}
          aria-label="Diminuisci quantità"
        >
          <Minus className="w-4 h-4" style={{ color: theme.textSecondary }} />
        </button>

        <span className="w-8 text-center text-sm font-medium" style={{ color: theme.textColor }}>
          {item.quantity}
        </span>

        <button
          onClick={handleIncrement}
          className="p-1 rounded-full transition-colors"
          style={{ backgroundColor: `${theme.textColor}0d` }}
          aria-label="Aumenta quantità"
        >
          <Plus className="w-4 h-4" style={{ color: theme.textSecondary }} />
        </button>

        <button
          onClick={handleRemove}
          className="ml-2 p-1 rounded-full transition-colors hover:bg-red-500/20"
          aria-label="Rimuovi prodotto"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>

      <div className="text-right">
        <p className="text-sm font-semibold" style={{ color: theme.textColor }}>
          €{itemTotal.toFixed(2)}
        </p>
      </div>
    </div>
  )
}
